import React, { useEffect, useRef } from 'react';

const STEPS = [
  { num: '01', icon: '🏪', title: 'Set Up Your Store', desc: 'Configure your store profile, branches, operating hours, delivery zones, and staff roles in minutes from the Mart CMS portal. No technical expertise needed.' },
  { num: '02', icon: '📦', title: 'Add Products & Inventory', desc: 'Upload your catalogue with categories, variants, pricing, tax, and SKUs. Link vendors for automated purchase orders and set low-stock thresholds.' },
  { num: '03', icon: '🧾', title: 'Start Billing at the Counter', desc: 'Open your POS-Lite register, search or scan products, add to cart, and checkout in under 60 seconds. Supports all payment modes from day one.' },
  { num: '04', icon: '📱', title: 'Take Mobile & Delivery Orders', desc: 'Customers order via the Customer App. Orders flow into your dashboard in real time. Assign to delivery riders through the Porter App automatically.' },
  { num: '05', icon: '📊', title: 'Analyse & Grow', desc: 'View daily sales, order trends, top products, customer behaviour, and delivery performance on the live dashboard. Export to Excel or PDF with one click.' },
];

export default function HowItWorks() {
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); });
    }, { threshold: 0.1 });
    ref.current?.querySelectorAll('.reveal').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const nav = id => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section className="section" id="how-it-works" style={{ background: 'white' }} ref={ref}>
      <div className="container">
        <div className="reveal" style={{ textAlign: 'center', maxWidth: 560, margin: '0 auto 64px' }}>
          <span className="eyebrow">How It Works</span>
          <h2 className="t-section" style={{ color: '#0c1220' }}>Up and running in one day.</h2>
          <p style={{ fontSize: 16, color: '#6b7280', marginTop: 14, lineHeight: 1.7 }}>From store setup to your first live order — the average merchant goes live in under 24 hours.</p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {STEPS.map((s, i) => (
            <div key={s.num} className="reveal" style={{ transitionDelay: i * 0.08 + 's', display: 'flex', gap: 28, alignItems: 'flex-start', position: 'relative', paddingBottom: i < STEPS.length - 1 ? 0 : 0 }}>
              {/* Left — number + connector */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: 72 }}>
                <div style={{
                  width: 72, height: 72, borderRadius: '50%',
                  background: 'white', border: '2px solid #e5e7eb',
                  boxShadow: '0 0 0 6px #f9fafb',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 28, flexShrink: 0, zIndex: 1, position: 'relative',
                }}>{s.icon}</div>
                {i < STEPS.length - 1 && (
                  <div style={{ width: 2, flex: 1, minHeight: 48, background: 'repeating-linear-gradient(180deg,#2563eb 0,#2563eb 6px,transparent 6px,transparent 14px)', opacity: 0.3, margin: '4px 0' }} />
                )}
              </div>

              {/* Right — content */}
              <div style={{ flex: 1, paddingBottom: i < STEPS.length - 1 ? 36 : 0 }}>
                <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 11, fontWeight: 700, color: '#2563eb', letterSpacing: '1.2px', marginBottom: 8 }}>{s.num}</div>
                <h3 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: 20, color: '#111827', marginBottom: 10, letterSpacing: '-0.4px' }}>{s.title}</h3>
                <p style={{ fontSize: 15, color: '#6b7280', lineHeight: 1.7, maxWidth: 580 }}>{s.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="reveal" style={{ marginTop: 60, textAlign: 'center' }}>
          <div style={{ background: 'linear-gradient(135deg,#0c1220,#1e2d45)', borderRadius: 20, padding: '44px 40px', display: 'inline-block', maxWidth: 580, width: '100%' }}>
            <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 24, fontWeight: 800, color: 'white', marginBottom: 10, letterSpacing: '-0.5px' }}>Ready to modernise your store?</div>
            <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.55)', marginBottom: 24, lineHeight: 1.7 }}>Book a personalised demo and we'll walk you through the entire platform using your own product catalogue.</p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button className="btn btn-blue" onClick={() => nav('contact')}>Book a Free Demo →</button>
              <button className="btn btn-ghost" onClick={() => nav('modules')}>Explore All Modules</button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
